import { Button, CircularProgress, Grid } from "@material-ui/core";
import * as React from "react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import {
	EmailAddressFormValues,
	INITIAL_VALUES,
} from "../../../emailTopics/models/EmailAddress";
import {
	RECIPIENT_TYPE,
	type RecipientType,
} from "../../../emailTopics/models/EmailAddressTopic";
import {
	setEmailAddressTopics,
	upsertEmailAddress,
} from "../../../emailTopics/repositories/EmailTopicsRepository";
import { useEmailAddressesContext } from "../../context/EmailAddressesContext";
import { EmailAddressFormFields } from "./EmailAddressFormFields";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditEmailAddress = ({ show, onHide }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const {
		state: { addressSelected, topics, addressTopics },
	} = useEmailAddressesContext();

	const { control, handleSubmit, reset } = useForm<EmailAddressFormValues>({
		defaultValues: INITIAL_VALUES,
	});

	const onSubmit: SubmitHandler<EmailAddressFormValues> = async (data) => {
		if (!addressSelected) return;

		setIsSubmitLoading(true);
		try {
			await upsertEmailAddress({
				EmailAddressId: addressSelected.EmailAddressId,
				Address: data.Address,
			});

			await setEmailAddressTopics({
				EmailAddressId: addressSelected.EmailAddressId,
				TopicIdsTO: data.TopicIdsTO,
				TopicIdsCC: data.TopicIdsCC,
				TopicIdsBCC: data.TopicIdsBCC,
				IncludeSystem: false,
			});

			onHide(true);
		} catch (e: any) {
			setError(e?.message || "Error saving email address");
		} finally {
			setIsSubmitLoading(false);
		}
	};

	useEffect(() => {
		if (!show || !addressSelected) return;

		const idsFor = (type: RecipientType) =>
			addressTopics
				.filter(
					(assignment) =>
						assignment.EmailAddressId === addressSelected.EmailAddressId &&
						assignment.RecipientType === type,
				)
				.map((assignment) => assignment.EmailTopicId);

		reset({
			EmailAddressId: addressSelected.EmailAddressId,
			Address: addressSelected.Address,
			TopicIdsTO: idsFor(RECIPIENT_TYPE.TO),
			TopicIdsCC: idsFor(RECIPIENT_TYPE.CC),
			TopicIdsBCC: idsFor(RECIPIENT_TYPE.BCC),
		});
	}, [show, addressSelected, addressTopics]);

	return (
		<>
			<ErrorModal error={error} onHide={() => setError("")} />
			<MesfModal
				title="EDIT EMAIL ADDRESS"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-email-address-modal"
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px 0" }}>
						<EmailAddressFormFields control={control} topics={topics} />
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid
							container
							spacing={2}
							justifyContent="flex-end"
							style={{ padding: "1rem" }}
						>
							<Grid item md={3} xs={12}>
								<Button
									fullWidth
									variant="contained"
									onClick={() => onHide(false)}
									disabled={isSubmitLoading}
								>
									CANCEL
								</Button>
							</Grid>
							<Grid item md={3} xs={12}>
								<Button
									fullWidth
									type="submit"
									variant="contained"
									color="primary"
									disabled={isSubmitLoading}
								>
									{isSubmitLoading ? <CircularProgress size={24} /> : "SAVE"}
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
		</>
	);
};
